/**
 * Compatibility between the stored UI module configurations and the controls
 * of the configuration forms.
 *
 * The customer application reads the saved configurations at runtime, so what
 * the super-user forms save must keep the runtime behaviour of what the OLD
 * forms saved. The old forms were loosely typed (text inputs and selects with
 * string values): a value the admin edited was saved as a string ("10",
 * "true", "4"), a value the admin left alone stayed exactly as stored. The sh-*
 * controls are strictly typed (numbers, booleans), so:
 *
 * - on LOAD a value is normalised to the type the control understands (so it
 *   shows), and the raw stored value is remembered;
 * - on SAVE a value the user did not change goes back as the ORIGINAL raw value
 *   (same type), and a changed value is written in the type the old form
 *   produced.
 */

/** How one field is normalised for its control and written back when edited. */
export interface ConfigValueRule {
  /** The raw stored value in the shape the control understands. */
  normalize(raw: unknown): unknown;
  /** The (normalised) edited value in the shape the old form saved. */
  toSaved(value: unknown): unknown;
}

/** The rules of the fields of one configuration object, by property name. */
export type ConfigRules = Record<string, ConfigValueRule>;

/** The raw value of a field when the form loaded. */
export interface ConfigOriginalValue {
  present: boolean;
  raw: unknown;
}

/** The raw values of the fields of one configuration object. */
export type ConfigSnapshot = Record<string, ConfigOriginalValue>;

/** A non-empty numeric string becomes a number, "" becomes null. */
export function normalizeNumeric(raw: unknown): unknown {
  if (typeof raw === 'string') {
    const text = raw.trim();
    if (text === '') {
      return null;
    }
    const value = Number(text);
    return Number.isFinite(value) ? value : raw;
  }
  return raw;
}

/** The strings "true" and "false" become booleans. */
export function normalizeBoolean(raw: unknown): unknown {
  if (typeof raw === 'string') {
    const text = raw.trim().toLowerCase();
    if (text === 'true') {
      return true;
    }
    if (text === 'false') {
      return false;
    }
  }
  return raw;
}

const isEmpty = (value: unknown) => value === null || value === undefined;

/** A toggle, or a select of booleans: a boolean, written back as one. */
export const BOOLEAN_RULE: ConfigValueRule = {
  normalize: normalizeBoolean,
  toSaved: (value) => value,
};

/**
 * `sortFieldOrder`: the old select (`<option [value]="true">`) saved the
 * strings "true" / "false" once changed; the customer application reads
 * `sortFieldOrder === true`, so the string "true" sorts descending there. An
 * edited value is therefore saved as that string (null stays null).
 */
export const SORT_FIELD_ORDER_RULE: ConfigValueRule = {
  normalize: normalizeBoolean,
  toSaved: (value) => (isEmpty(value) ? null : String(value)),
};

/**
 * `pageSize`, `order` and the `sortField` of a field configuration: the old
 * text input saved a string once edited, "" when cleared.
 */
export const NUMERIC_TEXT_RULE: ConfigValueRule = {
  normalize: normalizeNumeric,
  toSaved: (value) => (isEmpty(value) ? '' : String(value)),
};

/** `searchOperator`: the old operator select saved a numeric string once picked. */
export const SEARCH_OPERATOR_RULE: ConfigValueRule = {
  normalize: normalizeNumeric,
  toSaved: (value) => (isEmpty(value) ? null : String(value)),
};

/** Calendar `dayStart` / `dayEnd`: the old time input saved "HH:mm", "" when cleared. */
export const TIME_RULE: ConfigValueRule = {
  normalize: (raw) => raw,
  toSaved: (value) => (isEmpty(value) ? '' : String(value)),
};

/** The rules of a set of toggles (or boolean selects). */
export function booleanRules(...keys: string[]): ConfigRules {
  return Object.fromEntries(keys.map((key) => [key, BOOLEAN_RULE]));
}

/** The time of day the calendar day starts or ends at: HH:mm, or empty. */
export const TIME_PATTERN = /^([01]\d|2[0-4]):[0-5]\d$/;

/** Whether a calendar day start / end is empty or a valid HH:mm. */
export function isValidTime(value: unknown): boolean {
  return isEmpty(value) || value === '' || TIME_PATTERN.test(String(value));
}

/** Both are the same value, an empty value (null, undefined) being one value. */
function sameValue(a: unknown, b: unknown): boolean {
  return isEmpty(a) ? isEmpty(b) : a === b;
}

/** The raw values of the fields (that exist) of an object. */
export function captureSnapshot(
  target: object,
  rules: ConfigRules
): ConfigSnapshot {
  const snapshot: ConfigSnapshot = {};
  for (const key of Object.keys(rules)) {
    const present = key in target;
    snapshot[key] = {
      present,
      raw: present ? (target as Record<string, unknown>)[key] : undefined,
    };
  }
  return snapshot;
}

/**
 * A copy of the object with the fields normalised for their controls (fields
 * the object does not have stay absent).
 */
export function normalizeFields<T extends object>(
  target: T,
  rules: ConfigRules
): T {
  const copy = { ...target } as Record<string, unknown>;
  for (const [key, rule] of Object.entries(rules)) {
    if (key in copy) {
      copy[key] = rule.normalize(copy[key]);
    }
  }
  return copy as T;
}

/**
 * A copy of the object as it is to be saved: a field the user did not change
 * (its normalised value equals the normalised original) gets the original
 * raw value back; a changed field is written the way the old form wrote it. A
 * field without an original (a new object) counts as changed from null.
 */
export function applySaveCompat<T extends object>(
  value: T,
  snapshot: ConfigSnapshot | undefined,
  rules: ConfigRules
): T {
  const copy = { ...value } as Record<string, unknown>;
  for (const [key, rule] of Object.entries(rules)) {
    if (!(key in copy)) {
      continue;
    }

    const original = snapshot?.[key];
    const current = rule.normalize(copy[key]);
    const originalValue = original?.present
      ? rule.normalize(original.raw)
      : null;

    if (sameValue(current, originalValue)) {
      // Unchanged: the original as stored; no original: what the form has.
      copy[key] = original?.present ? original.raw : copy[key];
    } else {
      copy[key] = rule.toSaved(current);
    }
  }
  return copy as T;
}

/** Whether a field still has the value it had when the form loaded. */
export function isFieldUnchanged(
  value: object,
  snapshot: ConfigSnapshot | undefined,
  rules: ConfigRules,
  key: string
): boolean {
  const rule = rules[key];
  const original = snapshot?.[key];
  const current = rule.normalize((value as Record<string, unknown>)[key]);
  const originalValue = original?.present ? rule.normalize(original.raw) : null;
  return sameValue(current, originalValue);
}

/**
 * Remembers the original raw values of the configuration objects (by object
 * identity) of one form: `load` before the controls see an object, `forSave`
 * when the form's values are saved.
 */
export class ConfigValueCompat {
  private readonly _originals = new WeakMap<object, ConfigSnapshot>();

  constructor(public readonly rules: ConfigRules) {}

  /**
   * Remembers the raw values of `target` (once, under `key`, which is the
   * target itself by default) and normalises the target IN PLACE.
   */
  load<T extends object>(target: T, key: object = target): T {
    if (!this._originals.has(key)) {
      this._originals.set(key, captureSnapshot(target, this.rules));
    }
    Object.assign(target, normalizeFields(target, this.rules));
    return target;
  }

  /** Remembers a snapshot (`captureSnapshot`) under `key`, an object other than the target. */
  remember(key: object, snapshot: ConfigSnapshot): void {
    this._originals.set(key, snapshot);
  }

  /** The object as it is to be saved (a copy). */
  forSave<T extends object>(value: T, key: object = value): T {
    return applySaveCompat(value, this._originals.get(key), this.rules);
  }

  /** Like `forSave`, but writes the saved values into the object itself. */
  restoreInPlace<T extends object>(target: T, key: object = target): T {
    const saved = this.forSave(target, key) as Record<string, unknown>;
    for (const field of Object.keys(this.rules)) {
      if (field in saved) {
        (target as Record<string, unknown>)[field] = saved[field];
      }
    }
    return target;
  }

  /** Whether a field of the object still has its original value. */
  isUnchanged(value: object, field: string, key: object = value): boolean {
    const snapshot = this._originals.get(key);
    // Not loaded (yet): nothing to compare with, so nothing to complain about.
    return snapshot
      ? isFieldUnchanged(value, snapshot, this.rules, field)
      : true;
  }
}

/** The linked objects of a details configuration (`objectsConfig[]`). */
export const LINKED_OBJECT_RULES: ConfigRules = {
  order: NUMERIC_TEXT_RULE,
  pageSize: NUMERIC_TEXT_RULE,
  sortFieldOrder: SORT_FIELD_ORDER_RULE,
  ...booleanRules(
    'showExportButton',
    'showAddButton',
    'showLinkButton',
    'showBatchUpdateButton',
    'showDeleteButton',
    'showDuplicateButton',
    'enableMultiSort',
    'loadFromApi'
  ),
};

/** One field configuration (`displayFieldsConfiguration[]`). */
export const DISPLAY_FIELD_CONFIGURATION_RULES: ConfigRules = {
  sortField: NUMERIC_TEXT_RULE,
};

/** One row of the default filter of an overview configuration. */
export const DEFAULT_FILTER_FIELD_RULES: ConfigRules = {
  searchOperator: SEARCH_OPERATOR_RULE,
};

/** The overview configuration itself. */
export const OVERVIEW_CONFIGURATION_RULES: ConfigRules = {
  pageSize: NUMERIC_TEXT_RULE,
  sortOrder: BOOLEAN_RULE,
};

export const OVERVIEW_HEADER_RULES = booleanRules(
  'showSimpleSearch',
  'showAdvancedSearch',
  'showExportButton',
  'showAddButton',
  'showBatchUpdateButton'
);

export const OVERVIEW_SUMMARY_RULES = booleanRules('allowEdit');

/** The details configuration itself. */
export const DETAILS_CONFIGURATION_RULES = booleanRules(
  'showTasks',
  'showFiles',
  'showMessages',
  'showNotes',
  'showChat',
  'showInvoices'
);

export const DETAILS_HEADER_RULES = booleanRules(
  'showAddButton',
  'showDeleteButton',
  'showDuplicateButton',
  'showExportButton',
  'showViewChangesButton'
);

export const CALENDAR_HEADER_RULES = booleanRules(
  'showAddButton',
  'showExportButton',
  'showSimpleSearch',
  'showAdvancedSearch'
);

/** The calendar overview: the times of the day. */
export const CALENDAR_OVERVIEW_RULES: ConfigRules = {
  dayStart: TIME_RULE,
  dayEnd: TIME_RULE,
};
